import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import V2AuthButton from "./components/V2AuthButton";

export default function V2VerifyEmail() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading, success, error
  const [error, setError] = useState("");

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus("error");
        setError(i18n.t("auth.verifyEmail.noToken"));
        return;
      }

      try {
        await betaAPI.verifyEmail({ token });

        // Update user in localStorage if logged in (must happen before showing success)
        const authToken = localStorage.getItem("authToken");
        if (authToken) {
          try {
            const { data: freshUser } = await betaAPI.getCurrentUser();
            // Always force is_verified: true — don't trust the API response
            // to have propagated the verification status yet
            localStorage.setItem(
              "user",
              JSON.stringify({ ...freshUser, is_verified: true })
            );
          } catch (err) {
            console.error("Failed to fetch updated user:", err);
            // Fallback: manually update is_verified in existing user data
            const existingUser = JSON.parse(
              localStorage.getItem("user") || "{}"
            );
            if (existingUser.email) {
              localStorage.setItem(
                "user",
                JSON.stringify({ ...existingUser, is_verified: true })
              );
            }
          }
        }

        setStatus("success");
        showToast.success(i18n.t("auth.verifyEmail.verifiedToast"));
      } catch (err) {
        console.error("Verification error:", err);
        setStatus("error");
        setError(err.message || i18n.t("auth.verifyEmail.failed"));
      }
    };

    verifyEmail();
  }, [token]);

  const handleContinue = () => {
    const authToken = localStorage.getItem("authToken");
    const pendingInvite = sessionStorage.getItem("pendingInviteUrl");
    if (pendingInvite) {
      sessionStorage.removeItem("pendingInviteUrl");
      navigate(pendingInvite, { replace: true });
    } else if (authToken) {
      navigate("/v2", { replace: true });
    } else {
      navigate("/v2/auth/login", { replace: true });
    }
  };

  // Loading state
  if (status === "loading") {
    return (
      <>
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#F3F4F6]">
            <Loader size={28} className="animate-spin text-[#6B7280]" />
          </div>
        </div>

        <div className="text-center">
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {t("auth.verifyEmail.verifyingTitle")}
          </h1>
          <p className="text-sm text-[#6B7280]">
            {t("auth.verifyEmail.verifyingDescription")}
          </p>
        </div>
      </>
    );
  }

  // Success state
  if (status === "success") {
    return (
      <>
        <div className="mb-6 flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <CheckCircle size={28} className="text-green-600" />
          </div>
        </div>

        <div className="mb-6 text-center">
          <h1 className="mb-2 text-xl font-bold text-[#111827]">
            {t("auth.verifyEmail.successTitle")}
          </h1>
          <p className="text-sm text-[#6B7280]">
            {t("auth.verifyEmail.successDescription")}
          </p>
        </div>

        <V2AuthButton variant="primary" onClick={handleContinue}>
          {t("auth.verifyEmail.continueToDashboard")}
        </V2AuthButton>
      </>
    );
  }

  // Error state
  return (
    <>
      <div className="mb-6 flex justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <XCircle size={28} className="text-red-600" />
        </div>
      </div>

      <div className="mb-6 text-center">
        <h1 className="mb-2 text-xl font-bold text-[#111827]">
          {t("auth.verifyEmail.failedTitle")}
        </h1>
        <p className="text-sm text-[#6B7280]">{error}</p>
      </div>

      <V2AuthButton
        variant="secondary"
        onClick={() => navigate("/v2/auth/login", { replace: true })}
      >
        {t("auth.common.backToLogin")}
      </V2AuthButton>

      <div className="mt-4 text-center">
        <p className="text-sm text-[#6B7280]">
          {t("auth.verifyEmail.needNewLink")}{" "}
          <Link
            to="/v2/auth/verify-email-sent"
            className="font-medium text-[#E07A3A] transition-colors hover:text-[#D66B2E]"
          >
            {t("auth.verifyEmail.resend")}
          </Link>
        </p>
      </div>
    </>
  );
}
